import { startGame } from '@sl/base/main';
import { Scene } from '@sl/base/scene';
import { Bonus } from '@sl/games/london/bonus';
import { Payline } from '@sl/base/logic/payline';
import { Winning } from '@sl/base/logic/winning';

class GameScene extends Scene {
    update() {
        super.update();
    }

    init() {
        super.init();

        this.letInitialize(() => {
            this.winTable = undefined;
            this.winText = undefined;
            this.amountWonStep = 15;
            this.amountWonTimeout = 40;
            this.amountWonFadeInSpeed = 300;
            
            this.positionerContainer = this.add.container();
            this.createBg();

            this.slots.symbolHeight = 152;
            this.slots.symbolWidth = 125;
            this.slots.spinningRowsCount = 6;
            this.slots.symbolsOffsetLeft = 160;
            this.slots.visibleRowsCount = 3;
            this.slots.reelsOffsetTop = 335;
            this.slots.symbolBlurFrame = 25;
            this.slots.frameTopOffset = -35;
            this.slots.frameLeftOffset = 0;
            this.slots.spaceBetweenReels = 23;
            this.slots.reelBackgroundHorizontalPadding = 25;
            this.slots.symbolsFadeOffsetY = -3;
            this.slots.symbolsFade = 'symbols_fade';
            this.slots.overspinSound = this.sound.add('overspin', {"loop":false, "volume": 1});

            this.slots.createSlotMachine();
            this.addGameLogo();
            this.addSymbolsAnimations();

            this.bonus = new Bonus(this);
            this.payline = new Payline(this);
            this.winning = new Winning(this);

            this.payline.drawPaylines();

            this.spinSound = this.sound.add('spin', {"loop":false, "volume": 1});

            this.createWinningsContainer();
            this.spritesReady = true;
            this.baseGamePositioner();
        });
    }

    afterPurchase() {
        this.winning.removeTimers();
        this.slots.spin();
    }

    afterStep() {
        const { scenario } = this.request;
        
        if (scenario) {
            if(!this.skip && scenario.freespins && parseInt(scenario.freespins.current) === 1) {
                this.bonus.init(() => {
                    this.afterPurchase();
                });
            } else {
                if (scenario.reels) {
                    this.afterPurchase();
                } 
            }

            return;
        }

        if(this.request.previousScenario && this.request.previousScenario.freespins) {
            this.bonus.after(() => {
                this.request.spinFinished();
            });

            return;
        }

        this.request.spinFinished();
    }

    createWinningsContainer() {
        this.winningsContainer = this.add.container();
        this.winningsContainer.alpha = 0;
    }

    overspinAnimationConfig(overspinReel) {
        var curve = new Phaser.Curves.Path(0, 0);

        curve.lineTo(this.slots.symbolWidth, 0);
        curve.lineTo(this.slots.symbolWidth, this.slots.frameMaskHeight);
        curve.lineTo(0, this.slots.frameMaskHeight);
        curve.lineTo(0, 0);
        
        return {
            x: overspinReel.x - this.slots.symbolWidth / 2,
            y: overspinReel.y - this.slots.frameMaskHeight / 2,
            blendMode: 'ADD',
            gravityY: 300,
            lifespan: 2000,
            quantity: 3,
            accelerationX: -5,
            rotate: { min: -180, max: 180 },
            scale: { start: 0.5, end: 0 },
            alpha: { min: 0.2, max: 0.5 },
            emitZone: {
                source: curve,
                type: 'random',
                quantity: 5
            }
        }
    }

    createBg() {
        this.bg = this.add.sprite(0, 0, 'bg').setOrigin(0.5, 0);
        this.positionerContainer.add(this.bg);

        this.headlights = this.add.sprite(0, 0, 'headlights').setOrigin(0, .5);
        this.headlights.tween = this.tweens.add({
            targets: this.headlights,
            alpha: { from: 0.5, to: 1 },
            alpha: { start: 0.5, to: 1 },
            ease: 'Linear',
            duration: 1000,
            repeat: -1,
            yoyo: true
        });

        this.chimneys = [];

        this.chimney_1 = this.add.sprite(1177, -20, 'chimney_smoke');
        this.chimneys.push(this.chimney_1);
        this.chimney_2 = this.add.sprite(1388, 20, 'chimney_smoke');
        this.chimneys.push(this.chimney_2);
        
        this.anims.create({
            key: 'chimney_smoke',
            frameRate: 18,
            yoyo: 0,
            repeat: -1,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('chimney_smoke', {
                start: 0,
                end: 48,
            }),
        });

        for(let index in this.chimneys) {
            this.anims.play('chimney_smoke', this.chimneys[index]);
        }

        this.steams = [];

        this.steam_1 = this.add.sprite(0, 0, 'corner_steam');
        this.steams.push(this.steam_1);
        this.steam_2 = this.add.sprite(0, 0, 'corner_steam');
        this.steam_2.flipX = true;
        this.steams.push(this.steam_2);

        this.anims.create({
            key: 'corner_steam',
            frameRate: 18,
            yoyo: 0,
            repeat: -1,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('corner_steam', {
                start: 0,
                end: 49,
            }),
        });

        for(let index in this.steams) {
            this.anims.play('corner_steam', this.steams[index]);
        }
    }

    addGameLogo() {
        this.logo = this.add.sprite(0, 0, 'logo').setOrigin(0.5, 0.5);

        this.anims.create({
            key: 'logo',
            frameRate: 20,
            yoyo: 0,
            repeat: -1,
            repeatDelay: 3000,
            skipMissedFrames: true,
            frames: this.anims.generateFrameNumbers('logo', {
                start: 0,
                end: 32,
            }),
        });

        this.anims.play('logo', this.logo);
    }

    afterSpin() {
        this.request.step();
    }

    addSymbolsAnimations() {
        for(const [ reelIndex, reel ] of Object.entries(this.slots.reelsContainersObjects)) {
            for(const symbolIndex of Object.keys(reel.symbols)) {
                let symbol = reel.symbols[symbolIndex];
                var config = {
                    key: `${symbol.texture.key}`,
                    skipMissedFrames: true,
                    frames: this.anims.generateFrameNumbers(`${symbol.texture.key}`, {
                        start: 0,
                        end: 24,
                    }),
                    frameRate: 20,
                    yoyo: 0,
                    repeat: 0
                };
                this.anims.create(config);
                symbol.anims.load(`${symbol.texture.key}`);
            }
        }
    }

    showWinning(text, callback) {
        let win = 'Mega';

        if(!text) {
            var winningAmount = this.request.scenario.amount_won;

            if(!winningAmount) {
                this.winning.playPaylines();

                if(callback) {
                    callback();
                }

                return;
            }

            win = 'Regular';

            if(this.request.scenario.tags.big_win) {
                win = 'Big';
            }
        }

        this.prepareWinningContainer(win);

        this.winning.playPaylines();

        if(callback) {
            callback();
        }
    }

    customPaylineSound(symbolObject) {
        if(symbolObject.symbolName === 'scatter') {
            this.sound.play('Mega');
        } else {
            this.playWinlineSound();
        }
    }

    customStopAnimation(reelIndex) {
        this.sound.play('reel_stop');
    }

    winningOnPayline(payline, match) {
        this.destroyWinningOnPayline();

        if(match.type !== 'row') {
            return;
        }

        const symbol = this.slots.getSymbolOnReelPosition(3, payline.midPosition)

        let winning = General.format_currency(match.multiplier * Config.current_amount.amount).replace(Config.currency.symbol, '');

        let amountContainer = this.winning.createWinningFromSprites(winning, 0.4);

        amountContainer.x = symbol.x;
        amountContainer.y = symbol.y;

        amountContainer.name = 'win_amount';
        amountContainer.setScale(0);

        this.slots.reelsContainer.add(amountContainer);
        
        amountContainer.tween = this.tweens.add({
            targets: [amountContainer],
            scale: 1,
            ease: 'Back',
            duration: 300
        });
    }

    destroyWinningOnPayline() {
        let winAmount = this.slots.reelsContainer.getByName('win_amount');

        if(winAmount) {
            winAmount.destroy();
        }
    }

    prepareWinningContainer() {
        this.hideWinTable();

        this.winningsContainer.alpha = 0;
    }

    hideWinTable() {
        if(this.winningsContainer && this.winningsContainer.alpha) {
            if(this.winningsContainer.tween && this.winningsContainer.tween.isPlaying()) {
                this.winningsContainer.tween.stop();
                this.winningsContainer.tween.remove();
            }
        
            this.winningsContainer.alpha = 0;

            for(const [symbolIndex, sprite] of Object.entries(this.winningsContainer.getAll())) {
                if(sprite.tween && sprite.tween.isPlaying()) {
                    sprite.tween.stop();
                    sprite.tween.remove();
                }
            }
        }
    }

    drawPaylines() {
        const halfSymbolHeight = this.slots.symbolHeight / 2;
        const offsetY = this.slots.frame.y - 10;
        const x = this.slots.frame.x - this.slots.frameLeftOffset - 1;

        this.payline.paylines[1] = this.add.sprite(x, offsetY, 'payline_01_02_03').setOrigin(0.5, 0.5);
        this.payline.paylines[1].midPosition = 1;
        this.payline.paylines[2] = this.add.sprite(x, offsetY - this.slots.symbolHeight, 'payline_01_02_03').setOrigin(0.5, 0.5);
        this.payline.paylines[2].midPosition = 0;
        this.payline.paylines[3] = this.add.sprite(x, offsetY + this.slots.symbolHeight, 'payline_01_02_03').setOrigin(0.5, 0.5);
        this.payline.paylines[3].midPosition = 2;
        this.payline.paylines[4] = this.add.sprite(x, offsetY, 'payline_04').setOrigin(0.5, 0.5);
        this.payline.paylines[4].midPosition = 2;
        this.payline.paylines[5] = this.add.sprite(x, offsetY, 'payline_05').setOrigin(0.5, 0.5);
        this.payline.paylines[5].midPosition = 0;
        this.payline.paylines[6] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_06_09').setOrigin(0.5, 0.5);
        this.payline.paylines[6].midPosition = 2;
        this.payline.paylines[7] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_07_08').setOrigin(0.5, 0.5);
        this.payline.paylines[7].midPosition = 0;
        this.payline.paylines[8] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_07_08').setOrigin(0.5, 0.5);
        this.payline.paylines[8].midPosition = 1;
        this.payline.paylines[9] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_06_09').setOrigin(0.5, 0.5);
        this.payline.paylines[9].midPosition = 1;
        this.payline.paylines[10] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_10_18').setOrigin(0.5, 0.5);
        this.payline.paylines[10].midPosition = 1;
        this.payline.paylines[11] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_11_19').setOrigin(0.5, 0.5);
        this.payline.paylines[11].midPosition = 1;
        this.payline.paylines[12] = this.add.sprite(x, offsetY, 'payline_12').setOrigin(0.5, 0.5);
        this.payline.paylines[12].midPosition = 0;
        this.payline.paylines[13] = this.add.sprite(x, offsetY, 'payline_13').setOrigin(0.5, 0.5);
        this.payline.paylines[13].midPosition = 2;
        this.payline.paylines[14] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_14_21').setOrigin(0.5, 0.5);
        this.payline.paylines[14].midPosition = 0;
        this.payline.paylines[15] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_15_20').setOrigin(0.5, 0.5);
        this.payline.paylines[15].midPosition = 2;
        this.payline.paylines[16] = this.add.sprite(x, offsetY, 'payline_16').setOrigin(0.5, 0.5);
        this.payline.paylines[16].midPosition = 0;
        this.payline.paylines[17] = this.add.sprite(x, offsetY, 'payline_17').setOrigin(0.5, 0.5);
        this.payline.paylines[17].midPosition = 2;
        this.payline.paylines[18] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_10_18').setOrigin(0.5, 0.5);
        this.payline.paylines[18].midPosition = 2;
        this.payline.paylines[19] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_11_19').setOrigin(0.5, 0.5);
        this.payline.paylines[19].midPosition = 0;
        this.payline.paylines[20] = this.add.sprite(x, offsetY - halfSymbolHeight, 'payline_15_20').setOrigin(0.5, 0.5);
        this.payline.paylines[20].midPosition = 1;
        this.payline.paylines[21] = this.add.sprite(x, offsetY + halfSymbolHeight, 'payline_14_21').setOrigin(0.5, 0.5);
        this.payline.paylines[21].midPosition = 1;
        this.payline.paylines[22] = this.add.sprite(x, offsetY, 'payline_22').setOrigin(0.5, 0.5);
        this.payline.paylines[22].midPosition = 2;
        this.payline.paylines[23] = this.add.sprite(x, offsetY, 'payline_23').setOrigin(0.5, 0.5);
        this.payline.paylines[23].midPosition = 0;
        this.payline.paylines[24] = this.add.sprite(x, offsetY, 'payline_24').setOrigin(0.5, 0.5);
        this.payline.paylines[24].midPosition = 1;
        this.payline.paylines[25] = this.add.sprite(x, offsetY, 'payline_25').setOrigin(0.5, 0.5);
        this.payline.paylines[25].midPosition = 1;
        
        for(const [ paylineIndex, payline ] of Object.entries(this.payline.paylines)) {
            payline.name = `payline_${paylineIndex}`;
            this.payline.paylinesContainer.add(payline);
            this.slots.reelsContainer.add(payline);
        }

        this.payline.paylinesContainer.y = this.slots.reelsOffsetTop;
        this.payline.hidePaylines();
    }

    playSpinSound() {
        this.spinSound.play();
    }

    muteSpinSound() {
        this.spinSound.stop();
    }

    playStopSound() {
        this.sound.play('click', {"volume":0.4});
    }

    playWinlineSound() {
        this.sound.play('gem');
    }

    skipAnimations() {
        this.winning.removeTimers();
        this.hideWinTable();
        this.skip = true;
        this.winning.doCallback();
    }

    symbolsFader() {
        const fader = this.slots.reelsContainer.getByName(this.slots.symbolsFade);
        fader.alpha = 1;
        this.slots.reelsContainer.bringToTop(fader);
    }

    destroySymbolsFader() {
        this.slots.reelsContainer.getByName(this.slots.symbolsFade).alpha = 0;
    }

    gamePositioner() {
        if(!this.spritesReady) {
            return;
        }

        this.logo.x = this.slots.frame.x + this.cameras.main.centerX;
        this.logo.y = 100 + this.slots.frameTopOffset;

        let offsetX = 0;

        if(this.orientationIsSetToPortrait()) {
            this.bg.setTexture('bg_portrait');
            this.slots.frameContainer.x = this.cameras.main.centerX + offsetX;
            this.slots.symbolsAndPaylinesContainer.x = this.cameras.main.centerX + offsetX;
            this.slots.reelsContainer.x = this.cameras.main.centerX + offsetX;

            this.slots.frameMaskOffset = 79;
            this.slots.frameMaskHeight = 447;
            this.slots.frameContainer.setScale(1.05);
            this.slots.symbolsAndPaylinesContainer.setScale(1.05);
            this.slots.reelsContainer.setScale(1.05);

            this.headlights.tween.stop();
            this.headlights.alpha = 0;
            this.steam_1.alpha = 0;
            this.steam_2.alpha = 0;

        } else {
            this.slots.frameMaskOffset = 72;
            this.slots.frameMaskHeight = 449;
            this.slots.frameContainer.setScale(1);
            this.slots.symbolsAndPaylinesContainer.setScale(1);
            this.slots.reelsContainer.setScale(1);
            this.bg.setTexture('bg');

            this.headlights.alpha = 1;
            this.headlights.tween.restart();
            this.headlights.x = 0;
            this.headlights.y = 580;
            
            Phaser.Display.Align.In.BottomRight(this.steam_1, this.add.zone(this.cameras.main.width / 2, this.cameras.main.height / 2, this.cameras.main.width, this.cameras.main.height), 0, -30);
            Phaser.Display.Align.In.BottomLeft(this.steam_2, this.add.zone(this.cameras.main.width / 2, this.cameras.main.height / 2, this.cameras.main.width, this.cameras.main.height), 0, -30);

            this.steam_1.alpha = 1;
            this.steam_2.alpha = 1;
        }
        
        this.slots.createReelsMask(offsetX);
    }
}

startGame(GameScene);